/**
 * Llamar a una edge function reintentando SOLO cuando la petición no llegó a salir.
 *
 * EL CASO REAL. El escaneo de facturas fallaba el primer intento de cada sesión,
 * siempre en el móvil. En `error_logs` los cuatro fallos son idénticos:
 *
 *     "Failed to send a request to the Edge Function"   ← FunctionsFetchError
 *     source: EdgeFunction:scan-invoice
 *     user_agent: ... iPhone OS 18_7 ... despia-iphone
 *
 * Ese error lo lanza supabase-js cuando el `fetch` del navegador falla: la
 * petición NO llegó al servidor. No hay respuesta que interpretar, no se ha
 * cobrado nada y nadie ha escrito en la base de datos. Repetirla es seguro y es
 * lo único que arregla el fallo.
 *
 * LO QUE NO SE REINTENTA, Y POR QUÉ IMPORTA. Si la función responde con un
 * código distinto de 2xx —créditos agotados, límite de peticiones, una factura
 * que no se puede leer— eso es un `FunctionsHttpError`: una RESPUESTA, no un
 * fallo de transporte. Repetirla no la va a mejorar; solo tarda el triple y
 * gasta el triple. Se devuelve tal cual y que decida quien llama.
 *
 * Tampoco se reintenta si el usuario canceló (`signal.aborted`).
 */
import { supabase } from "@/integrations/supabase/client";

export interface InvokeEdgeOptions {
  body?: unknown;
  /** Cancelación del usuario o timeout. Se pasa a cada intento. */
  signal?: AbortSignal;
  /** Intentos TOTALES, no reintentos. 1 = sin reintento. */
  attempts?: number;
  /** Espera antes de cada reintento. El último valor se reutiliza si hay más intentos. */
  delaysMs?: number[];
  /** Para avisar en pantalla («reintentando…»). `attempt` es el que va a empezar (2, 3…). */
  onRetry?: (attempt: number) => void;
  /** Inyectable en tests para no esperar de verdad. */
  sleep?: (ms: number) => Promise<void>;
}

export interface InvokeEdgeResult<T = unknown> {
  data: T | null;
  error: { message?: string; name?: string; context?: unknown } | null;
  /** Cuántos intentos se han hecho en total (1 = salió a la primera). */
  attemptsUsed: number;
}

/**
 * ¿El fallo es de transporte (la petición no salió) y por tanto repetible?
 *
 * Se mira el nombre de la clase Y el texto, porque el bundle de producción
 * renombra clases y `instanceof` a través de dos copias de la librería no es de
 * fiar. El texto es el que sale literal en los registros.
 */
export function isTransportFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: unknown; message?: unknown };
  if (e.name === "FunctionsFetchError" || e.name === "FunctionsRelayError") return true;
  const msg = typeof e.message === "string" ? e.message.toLowerCase() : "";
  if (!msg) return false;
  return (
    msg.includes("failed to send a request") ||
    msg.includes("failed to fetch") ||
    msg.includes("network error") ||
    msg.includes("load failed") // Safari/WKWebView dice esto cuando se corta la conexión
  );
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function invokeEdgeWithRetry<T = unknown>(
  fn: string,
  options: InvokeEdgeOptions = {},
): Promise<InvokeEdgeResult<T>> {
  const {
    body,
    signal,
    attempts = 3,
    delaysMs = [800, 2000],
    onRetry,
    sleep = defaultSleep,
  } = options;

  const total = Math.max(1, attempts);
  let last: InvokeEdgeResult<T> = { data: null, error: null, attemptsUsed: 0 };

  for (let attempt = 1; attempt <= total; attempt++) {
    const { data, error } = await supabase.functions.invoke(fn, {
      body: body as never,
      ...(signal ? { signal } : {}),
    });
    last = { data: (data as T) ?? null, error: error ?? null, attemptsUsed: attempt };

    if (!error) return last;
    if (signal?.aborted) return last;          // lo canceló el usuario: no insistir
    if (!isTransportFailure(error)) return last; // es una RESPUESTA, no un fallo de red
    if (attempt >= total) return last;

    const wait = delaysMs[Math.min(attempt - 1, delaysMs.length - 1)] ?? 800;
    onRetry?.(attempt + 1);
    await sleep(wait);
    if (signal?.aborted) return last;
  }

  return last;
}
